import { BoxV2 as Box, Button, Flex, useIdleTimer } from 'portal-commons';
import { useOktaAuth } from '@okta/okta-react';
import { FC } from 'react';
import Modal from '@/components/Modal';

interface IdleWarningCountdownModalProps {
  'data-testid'?: string;
}

const IdleWarningCountdownModal: FC<IdleWarningCountdownModalProps> = ({
  'data-testid': testId = 'idleWarningCountdownModal',
}) => {
  const { authState, oktaAuth } = useOktaAuth();

  const { isIdle, formattedTimeLeft, stayLoggedIn, logout } = useIdleTimer({
    enabled: !!authState?.isAuthenticated,
    onLogout: () => {
      localStorage.clear();
      sessionStorage.setItem('logoutSuccess', 'true');
      oktaAuth?.signOut();
    },
  });

  const handleStayLoggedInClick = async () => {
    try {
      if (oktaAuth) {
        await oktaAuth.tokenManager.renew('accessToken');
        await oktaAuth.tokenManager.renew('idToken');
      }
      stayLoggedIn();
    } catch (err) {
      console.error('Failed to renew Okta session on button click:', err);
    }
  };

  // Render absolutely nothing when not idle.
  // This prevents second-by-second re-renders of the modal elements, buttons, and backdrops
  // which was causing conflicts/errors on pages containing Date Range Pickers.
  if (!isIdle) {
    return null;
  }

  return (
    <Modal
      open={isIdle}
      testid={testId}
      style={{
        zIndex: 3000,
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          rowGap: '8px',
        }}
      >
        {/* Countdown Timer Header */}
        <Flex
          data-testid="idleCountdownHeader"
          sx={{
            py: '12px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            data-testid="idleCountdownTimer"
            sx={{
              fontSize: '32px',
              fontWeight: 600,
              color: 't.black80',
              lineHeight: 'normal',
            }}
          >
            {formattedTimeLeft}
          </Box>
        </Flex>

        <Flex
          sx={{
            pt: '7px',
            pb: '11px',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          <Box
            data-testid="idleWarningDescription"
            sx={{
              fontSize: '18px',
              fontWeight: 400,
              color: '#333E41',
              textAlign: 'center',
              lineHeight: 'normal',
              fontFamily: "'Roboto', sans-serif",
            }}
          >
            Your session is expiring and you will soon be logged out. Click the
            button to stay logged in.
          </Box>
        </Flex>

        <Flex
          sx={{
            pt: '30px',
            pb: '40px',
            columnGap: '18px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            type="button"
            variant="outline"
            shape="rounded"
            size="medium"
            color="primary"
            onClick={logout}
            data-testid="idleLogOutButton"
          >
            Log Out
          </Button>
          <Button
            type="button"
            shape="rounded"
            size="medium"
            color="primary"
            onClick={handleStayLoggedInClick}
            data-testid="idleStayLoggedInButton"
          >
            Stay Logged In
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default IdleWarningCountdownModal;
